import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Choice, Information, LockClosed } from '@sas/react-svg';
import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useParentSize } from '../../../hooks/useParentSize';
import { usePreviousValue } from '../../../hooks/usePreviousValue';
import { useLanguage } from '../../../locale';
import { useComparator } from '../../../locale/sorting';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { Icon } from '../../Icon';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { Label } from '../../Label';
import { Flow } from '../../Layouts/Flow';
import { SearchField } from '../../SearchField';
import { GlobalSettingsKeys } from '../../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../../Settings/GlobalSettingsProvider';
import { TableRegions } from '../../Table';
import { Text } from '../../Text';
import { DEFAULT_INDENTATION, TreeTable, useTreeTableState } from '../../TreeTable';
import './DualSelectorLayout.css';
import cn from './DualSelectorLayout.css.json';

/**
 * In the mode of 'keep', an available item can be added from source to target multiple times.
 * This property indicates how to display duplicated items.
 * - 'single' indicates the duplicated item will be displayed as one item with counter in following parenttheses.
 * - 'multiple' indicates the duplicated item can be listed multiple times in selected list and the order is as the order it is added and the order can be adjusted by order buttons.
 *
 * For other modes, 'move' and 'disable', the source and target layouts display items like multiple display style in 'keep' mode but there is no duplicated items in them.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
export const isAlwaysSelectedItem = item => {
  // '_alwaysSelected' property is internally added for the items provided in alwaysSelected array whereas
  // 'alwaysSelected' property is added by the consumer in the target data items
  return (item._alwaysSelected || item.alwaysSelected) ?? false;
};
const getChildNodes = (key, forData) => {
  const index = parseInt(key);
  const data = forData[index];
  if (!data || !data.data.hasChildren) return [];
  const nodes = [];
  let nextLevel = (data.data.level || 0) + 1;
  for (let rowIndex = index + 1; rowIndex < forData.length; rowIndex++) {
    const {
      level,
      hasChildren
    } = forData[rowIndex].data;
    if (level === nextLevel) nodes.push(forData[rowIndex]);
    if (hasChildren) nextLevel++;
  }
  return nodes;
};
const getChildNodesRowKey = (key, forData) => {
  return getChildNodes(key, forData).map(node => node.key);
};
const getParentsRowKey = (key, forData) => {
  const keys = [];
  const index = parseInt(key);
  if (index !== -1) {
    const data = forData[index];
    let parentLevel = data.data.level - 1;
    for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
      const {
        level
      } = forData[rowIndex].data;
      if (level === parentLevel) {
        keys.push(forData[rowIndex].key);
        --parentLevel;
      }
    }
  }
  return keys;
};
const getDirectChildNodesRowKey = (key, forData) => {
  const keys = [];
  const index = parseInt(key);
  const data = forData[index];
  if (!data || !data.data.hasChildren) return [];
  const nextLevel = data.data.level + 1;
  for (let rowIndex = index + 1; rowIndex < forData.length; rowIndex++) {
    const {
      level
    } = forData[rowIndex].data;
    if (level === nextLevel) keys.push(forData[rowIndex].key);
    if (level > nextLevel) continue;
    if (level === data.data.level) break;
  }
  return keys;
};
const getAllLevelsChildNodesRowKey = (key, forData) => {
  let childRowKeys = getDirectChildNodesRowKey(key, forData);
  childRowKeys.forEach(childRowKey => {
    childRowKeys = [...childRowKeys, ...getAllLevelsChildNodesRowKey(childRowKey, forData)];
  });
  return childRowKeys;
};
const normalizedData = (expNodes, forData) => {
  const newData = [];
  let count = 0;
  for (let rowIndex = 0; rowIndex < forData.length; rowIndex++) {
    const rowData = forData[rowIndex];
    const {
      id,
      hasChildren
    } = rowData.data;
    const isExpanded = expNodes.has(id);
    newData[count] = {
      ...rowData
    };
    count++;
    if (!isExpanded && hasChildren) {
      const children = getAllLevelsChildNodesRowKey(rowData.key, forData);
      rowIndex += children.length;
    }
  }
  return newData;
};
export const DualSelectorLayout = /*#__PURE__*/forwardRef((props, ref) => {
  let columns = [];
  const getData = props.data;
  const initialDimension = {
    initialDimensions: {
      height: 200,
      width: 200
    }
  };
  const [setRef, dimensions] = useParentSize(initialDimension);
  const [searchValue, setSearchValue] = useState('');
  const [selectionState, setSelectionState] = useState({
    selectAll: false,
    selectedRows: undefined
  });
  const searchFieldRef = useRef(null);
  const localize = useNCTextLocalizer('DualSelector');
  const lang = useLanguage();
  const locale = lang.toLowerCase();
  const dt = useDesignTokens();
  const descriptionField = props.descriptionField;
  const getIconA11yInfo = useIconA11yInfo();
  const numberOfResults = props.numberOfResults;
  const expNodes = useRef(new Set());

  // with global settings
  const playVisualEffects = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true';
  useImperativeHandle(ref, () => ({
    reset: () => {
      setSearchValue('');
    }
  }));
  const defaultComparator = useComparator({
    toSortKey: t => t[props.dataFieldKeys[0]],
    direction: 'ascending'
  });
  const framerVariants = playVisualEffects ? {
    visible: {
      opacity: 1
    },
    hidden: {
      opacity: 0
    }
  } : undefined;
  const customCell = (columnIndex, rowIndex, _columnKey, _ref, disabled) => {
    const item = displayedData[rowIndex].data;
    const textIconTestId = `listWithIcon${rowIndex}`;
    const alwaysSelected = (props.tableName === 'target' && (item._alwaysSelected || item.alwaysSelected)) ?? false;
    const bCustomItem = props.tableName === 'target' && item._custom;
    const keyFieldValue = item[props.dataFieldKeys[columnIndex]];
    const count = displayedData[rowIndex].count;
    const targetDisplayText = keyFieldValue.toString() + (count > 1 ? ` (${count})` : '');
    const sourceDisplayText = keyFieldValue.toString();
    const isSelected = props.selections?.includes(rowIndex.toString());
    return /*#__PURE__*/_jsxs(motion.div, {
      variants: framerVariants,
      initial: "hidden",
      animate: "visible",
      exit: "hidden",
      className: cn.label,
      children: [item?.icon ? /*#__PURE__*/_jsx(Icon, {
        className: cn.listIcon,
        ...('icon' in item.icon ? {
          ...item.icon
        } : {
          icon: item.icon
        }),
        testId: postfixTestId(props.testId, textIconTestId),
        disabled: disabled
      }) : null, /*#__PURE__*/_jsxs(Flow, {
        orientation: 'vertical',
        spacing: "none",
        className: cn.text,
        children: [/*#__PURE__*/_jsx(Text, {
          className: cx(isSelected && cn.selected),
          wrapText: false,
          disabled: disabled,
          children: props.tableName === 'target' ? targetDisplayText : sourceDisplayText
        }), descriptionField && item[descriptionField] ? /*#__PURE__*/_jsx(Label, {
          wrapText: false,
          className: cx(cn.descriptionText, isSelected && cn.selected),
          testId: postfixTestId(props.testId, 'descriptionLabel'),
          disabled: disabled,
          children: item[descriptionField]
        }) : null]
      }), alwaysSelected ? /*#__PURE__*/_jsx(Icon, {
        className: cn.icon,
        icon: LockClosed,
        tooltip: localize('lockClosed.tooltip.label')
      }) : null, bCustomItem ? /*#__PURE__*/_jsx(Icon, {
        className: cn.icon,
        icon: Choice,
        tooltip: localize('customItem.choiceIcon.tooltip.label'),
        testId: postfixTestId(props.testId, 'choiceIcon')
      }) : null]
    });
  };
  const customTreeCell = (rowIndex, children) => {
    const item = displayedData[rowIndex].data;
    const alwaysSelected = (props.tableName === 'target' && (displayedData[rowIndex].data._alwaysSelected || displayedData[rowIndex].data.alwaysSelected)) ?? false;
    const bCustomItem = props.tableName === 'target' && displayedData[rowIndex].data._custom;
    const isSelected = props.selections?.includes(rowIndex.toString());
    return /*#__PURE__*/_jsxs(motion.div, {
      variants: framerVariants,
      initial: "hidden",
      animate: "visible",
      exit: "hidden",
      className: cn.label,
      children: [/*#__PURE__*/_jsxs(Flow, {
        orientation: 'vertical',
        spacing: "none",
        className: cn.text,
        children: [children, descriptionField && item[descriptionField] ? /*#__PURE__*/_jsx(Label, {
          wrapText: false,
          style: {
            marginInlineStart: `${DEFAULT_INDENTATION * (item.level || 0)}px`
          },
          className: cx(cn.descriptionText, isSelected && cn.selected),
          testId: postfixTestId(props.testId, 'descriptionLabel'),
          children: item[descriptionField]
        }) : null]
      }), alwaysSelected ? /*#__PURE__*/_jsx(Icon, {
        className: cn.icon,
        icon: LockClosed,
        tooltip: localize('lockClosed.tooltip.label')
      }) : null, bCustomItem ? /*#__PURE__*/_jsx(Icon, {
        className: cn.icon,
        icon: Choice,
        tooltip: localize('customItem.choiceIcon.tooltip.txt'),
        testId: postfixTestId(props.testId, 'choiceIcon')
      }) : null]
    });
  };

  /* -------------------------------------------
   * create column definitions - header
   * ---------------------------------------------*/
  const displaySourceHeaders = (dataKeys, textIconPresent, descriptionField) => {
    const columns = [];
    for (const [index, dataKey] of dataKeys.entries()) {
      columns.push({
        key: `_${index}`,
        label: dataKey,
        cellContent: props.type !== 'tree' && (textIconPresent || descriptionField) ? customCell : undefined,
        maximumWidth: 2000
      });
    }
    return columns;
  };

  /* -------------------------------------------
   * Check for zero state
   * ---------------------------------------------*/
  if (props.tableName === 'source' && props.type !== 'tree' && !props.disableSort) {
    getData.sort(props.sortFunction ?? defaultComparator);
  }
  if (props.tableName === 'target' && props.type !== 'tree') {
    props.sortFunction && getData.sort(props.sortFunction);
  }
  const dataValues = useMemo(() => {
    const length = getData.length;
    if (length > 0) {
      const originData = Object.keys(getData).map(key => getData[key]);
      if (props.tableName === 'source' && props.type === 'tree') {
        // always make sure source data is sorted - for correct parent/child dependency
        originData.sort((a, b) => {
          const key = 'index';
          let comparison = 0;
          if (a[key] > b[key]) {
            comparison = 1;
          } else if (a[key] < b[key]) {
            comparison = -1;
          }
          return comparison;
        });
      }
      return originData;
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData.length, getData]);
  const displayData = useMemo(() => {
    const generateMultipleDisplayData = (originData, type) => {
      const displayData = originData.map((item, index) => {
        // very first time - we index the original items for tree structures
        if (type === 'tree' && item.index === undefined) {
          item.index = index;
        }
        return {
          data: item,
          count: 1,
          originRowIndex: index,
          key: `${index}`
        };
      });
      return displayData.filter(item => {
        // get all children of the item
        const siblings = getAllLevelsChildNodesRowKey(item.key, displayData).filter(key => {
          // do not include folders
          const item = displayData.find(value => value.key === key);
          return item && !item.data.hasChildren;
        });
        const includeNode = !item.data.hasChildren || siblings.length > 0;
        // expand parent nodes if having matching children
        if (includeNode) {
          if (searchValue !== '' && item.data.hasChildren) {
            expNodes.current.add(item.data.id);
          }
        }
        return includeNode;
      }).map((item, index) => {
        return {
          ...item,
          key: `${index}`
        };
      });
    };
    if (props.tableName === 'target' && props.displayStyle === 'single') {
      return generateSingleDisplayData(dataValues);
    } else {
      return generateMultipleDisplayData(dataValues, props.type);
    }
  }, [props.tableName, props.displayStyle, props.type, dataValues, searchValue]);
  if (!(getData === null || getData === undefined && !props.alwaysSelected)) {
    if (dataValues.length > 0) {
      // Get keys array for number of columns
      const dataKeys = [];
      // In case of list display only one column based on props.dataFieldKeys
      if (props.dataFieldKeys !== null && props.dataFieldKeys !== undefined) {
        dataKeys.push(props.dataFieldKeys[0]);
        const textIconPresent = dataValues.some(dv => dv.icon && dv.icon.toString().length > 0);
        columns = displaySourceHeaders(dataKeys, textIconPresent, descriptionField);
        const infoIconPresent = dataValues.some(dv => dv.infoIcon);
        infoIconPresent && columns.push({
          key: 'infoIcon',
          label: '',
          width: 32
        });
        if (!infoIconPresent) {
          const infoTextPresent = dataValues.some(dv => dv.infoText);
          infoTextPresent && columns.push({
            key: 'info',
            label: '',
            width: 32
          });
        }
      }
    }
  }
  const selectionsInDisplayData = useMemo(() => props.selections ? getSelectionsInDisplayData(props.selections, dataValues, displayData) : undefined, [dataValues, displayData, props.selections]);
  useEffect(() => {
    if (props.disableFilter) {
      setSearchValue('');
    }
  }, [props.disableFilter]);
  const {
    onExpand,
    ...tableState
  } = useTreeTableState(new Set(displayData.filter(item => item.data.expanded).map(i => i.data.id)), {
    selectedRows: new Set(selectionsInDisplayData),
    selectAll: selectionState.selectAll
  });
  useEffect(() => {
    const setExpandedNodes = tableState.setExpandedNodes;
    setExpandedNodes(prevNodes => new Set([...prevNodes, ...expNodes.current]));
  }, [searchValue, tableState.setExpandedNodes]);
  const [displayedData, setDisplayedData] = useState(normalizedData(tableState.expandedNodes, displayData));
  const [numberOfRows, setNumberOfRows] = useState(displayedData.length);
  const prevDisplayedData = usePreviousValue(displayedData);
  useEffect(() => {
    const newDisplayData = normalizedData(tableState.expandedNodes, displayData);
    setDisplayedData(newDisplayData);
    setNumberOfRows(newDisplayData.length);
  }, [tableState.expandedNodes, displayData, searchValue]);
  useEffect(() => {
    const setSelectRows = tableState.setSelectRows;
    const scrollTo = tableState.scrollTo;
    const setExpandedNodes = tableState.setExpandedNodes;
    const clearMeasurementCache = tableState.clearMeasurementCache;
    clearMeasurementCache({
      startColumnIndex: 0,
      startRowIndex: 0
    });

    // expand a selected branch if required
    const expNodes = new Set(tableState.expandedNodes);
    selectionsInDisplayData?.forEach(selectedKey => {
      const parentKeys = getParentsRowKey(selectedKey, displayData);
      parentKeys.forEach(parentKey => {
        const parentNode = displayData.find(value => value.key === parentKey);
        const parentNodeId = parentNode?.data.id;
        if (!expNodes.has(parentNodeId)) {
          expNodes.add(parentNodeId);
        }
      });
    });
    const selectionsInDisplayedData = selectionsInDisplayData && getSelectionsInDisplayedData(selectionsInDisplayData, displayedData);
    if (expNodes.size !== tableState.expandedNodes.size) {
      setExpandedNodes(expNodes);
    }
    setSelectRows({
      selectedRows: new Set(selectionsInDisplayData),
      rowCount: dataValues.length
    });
    let scrollIndex = -1;
    if (props.focusAfterAction) {
      if (Array.isArray(selectionsInDisplayedData) && selectionsInDisplayedData.length > 0) {
        scrollIndex = selectionsInDisplayedData[0];
      } else if (props.isDataReset) {
        scrollIndex = 0;
      } else {
        // is the ability to track and scroll to the new item being added.
        // This is coded for target list in all modes and source list only in move mode.
        // Do we need this for source list in keep and disabled mode in future?
        if (Array.isArray(prevDisplayedData) && displayedData.length !== prevDisplayedData.length) {
          if (displayedData.length > prevDisplayedData.length) {
            scrollIndex = prevDisplayedData.findIndex((value, index) => value.data[props.dataFieldKeys[0]] !== displayedData[index].data[props.dataFieldKeys[0]]);
          } else {
            scrollIndex = -1;
          }
          if (scrollIndex === -1 && props.tableName === 'target') {
            scrollIndex = prevDisplayedData.length + 1;
          }
        }
      }
      if (scrollIndex !== -1 && scrollIndex !== tableState.preference.scrollPosition?.cell.rowIndex) {
        scrollTo({
          cell: {
            rowIndex: scrollIndex,
            colIndex: 1,
            region: TableRegions.Data
          },
          align: 'smart'
        });
      }
    }
  }, [prevDisplayedData, props.isDataReset, displayData, displayedData, props.dataFieldKeys, props.tableName, selectionsInDisplayData, props.focusAfterAction, dataValues.length, tableState.expandedNodes, tableState.setSelectRows, tableState.scrollTo, tableState.setSelectRow, tableState.setExpandedNodes, tableState.preference.scrollPosition, tableState.clearMeasurementCache]);
  const customItems = props.customItems;
  useEffect(() => {
    // Set the focus on the search field (if hideSearch is false)
    if (props.tableName === 'source' && searchFieldRef.current && !props.preventInitialAutoFocus) {
      searchFieldRef.current.focus();
    }
  }, [props.tableName, props.preventInitialAutoFocus]);
  useEffect(() => {
    /* -------------------------------------------
     * Get selected rows
     * ---------------------------------------------*/
    const getSelectedRows = (selectedRows, selectAll) => {
      const maxSelectedIndex = Math.max(-1, ...(selectedRows ?? []).map(item => parseInt(item)));
      const selections = maxSelectedIndex < displayData.length ? selectedRows : selectionsInDisplayData;
      const selectedOriginRows = (selections ?? []).map(row => {
        return displayData[parseInt(row)].originRowIndex.toString();
      });
      const selectionCallback = props.selectionCallback;
      const tableName = props.tableName;
      selectionCallback((!selectedRows && selectAll && selectAll !== 'mixed' ? [...dataValues.keys()].map(String) : selectedOriginRows).filter(rowKey => !dataValues[parseInt(rowKey)]?.hasChildren), tableName);
    };
    if (selectionState.selectedRows || selectionState.selectAll) {
      getSelectedRows(selectionState.selectedRows, selectionState.selectAll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionState]);
  const focusedCell = useRef({
    rowIndex: 0,
    region: props.data.length === 0 && props.hideSearch ? TableRegions.ZeroStateOverlay : TableRegions.Data,
    colIndex: 0
  });
  const tableStyles = renderInlineCssVars({
    'table-scroll-bar-space': '1rem',
    'tree-hover-bg': 'transparent'
  });
  function isSVGIconDescriptor(icon) {
    return icon.name !== undefined;
  }
  const defaultZeroStateText = props.tableName === 'target' ? localize('emptySelectedMessage.msg') : localize('emptyAvailableMessage.msg');
  const zeroStateText = props.zeroStateText ?? defaultZeroStateText;
  const key = props.dataFieldKeys[0];
  const getParentIds = id => {
    const ids = [];
    const index = displayedData.findIndex(value => {
      return value.key === id;
    });
    if (index === -1) return ids;
    const data = displayedData[index];
    let parentLevel = data.data.level - 1;
    for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
      const {
        level
      } = displayedData[rowIndex].data;
      if (level === parentLevel) {
        ids.push(displayedData[rowIndex].key);
        --parentLevel;
      }
    }
    return ids;
  };
  const getDirectChildIds = id => {
    const ids = [];
    const index = displayedData.findIndex(value => {
      return value.key === id;
    });
    const data = displayedData[index];
    if (!data || !data.data.hasChildren) return [];
    const nextLevel = data.data.level + 1;
    for (let rowIndex = index + 1; rowIndex < displayedData.length; rowIndex++) {
      const {
        level
      } = displayedData[rowIndex].data;
      if (level === nextLevel) ids.push(displayedData[rowIndex].key);
      if (level > nextLevel) continue;
      if (level === data.data.level) break;
    }
    return ids;
  };
  const getRowAttributes = id => {
    if (props.type === 'list') {
      const foundIndex = displayedData.findIndex(value => value.key === id);
      const dataLength = displayedData.length;
      return {
        level: null,
        setSize: dataLength,
        positionInSet: foundIndex + 1
      };
    }
    const dataItem = displayedData.find(value => value.key === id);
    if (!dataItem) return {};
    const level = typeof dataItem.data.level === 'number' && !isNaN(dataItem.data.level) ? dataItem.data.level + 1 : null;
    const rowParent = getParentIds(dataItem.key);
    let setSize, positionInSet;

    // Is a top level row
    if (rowParent.length === 0) {
      const topLevelItems = displayedData.filter(value => {
        const rowParent = getParentIds(value.key);
        return rowParent.length === 0;
      });
      setSize = topLevelItems.length;
      positionInSet = topLevelItems.findIndex(value => value.key === id) + 1;
    } else {
      const siblings = getDirectChildIds(rowParent[0]);
      setSize = siblings.length;
      positionInSet = siblings.indexOf(id) + 1;
    }
    return {
      level,
      setSize,
      positionInSet
    };
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cn.tableContent,
    "data-testid": props.testId,
    style: {
      ...renderInlineCssVars({
        'searchfield-spacing': dt.tokens['space-base-xs'],
        'icon-mg-inline': dt.tokens['space-base-xs'],
        'selected-fg': dt.themeVariant === 'high-contrast' ? dt.tokens['color-action-text-subtle-normal'] : dt.tokens['color-action-text-subtle-selected'],
        'font-size-s': dt.tokens['font-size-body-text-s']
      }, 'dualselector-list')
    },
    children: [!props.hideSearch ? /*#__PURE__*/_jsx("div", {
      className: cn.listSearchField,
      children: /*#__PURE__*/_jsx(SearchField, {
        testId: postfixTestId(props.testId, 'search'),
        className: cn.searchField,
        inputRef: props.tableName === 'source' ? searchFieldRef : undefined,
        value: searchValue,
        placeholder: localize('filter.placeholder.label'),
        onChange: e => {
          setSearchValue(e.value);
          expNodes.current = new Set();
          props.filterValue(e.value, props.tableName);
        },
        disabled: props.disableFilter,
        ariaLabel: props.searchAriaLabel,
        filterMode: true,
        numberOfResults: numberOfResults
      })
    }) : null, /*#__PURE__*/_jsx("div", {
      className: cn.tableList,
      onKeyUpCapture: e => {
        if (props.data.length === 0) e.stopPropagation();
      },
      children: /*#__PURE__*/_jsx("div", {
        className: cn.tableListInner,
        ref: setRef,
        children: /*#__PURE__*/_jsx(AnimatePresence, {
          mode: "wait",
          children: dimensions ? /*#__PURE__*/_createElement(TreeTable, {
            readOnly: !!props.readOnly,
            disabled: props.tableName === 'source' && !!props.disableList,
            role: props.type === 'tree' ? 'treegrid' : 'grid',
            ...tableState,
            style: tableStyles,
            testId: postfixTestId(props.testId, `${props.tableName}Table`),
            ariaLabel: props.tableAriaLabel,
            key: props.tableName,
            rowCount: numberOfRows,
            height: dimensions?.height,
            width: dimensions?.width,
            columns: columns,
            getRowAttributes: getRowAttributes,
            getRowKey: rowIndex => {
              if (rowIndex >= displayedData.length || rowIndex < 0) return '';
              if (props.type === 'tree') return displayedData[rowIndex].key;
              return rowIndex.toString();
            },
            getRowIndex: rowKey => {
              return parseInt(rowKey);
            },
            getDataNode: rowIndex => {
              // return a data node for trees - this will trigger the getDataNodeContainer method
              // - so we can render our custom tree structure
              return props.type === 'tree' && displayedData[rowIndex] ? displayedData[rowIndex].data : undefined;
            },
            getDataNodeContainer: customTreeCell,
            getChildNodesRowKey: rowKey => {
              return getChildNodesRowKey(rowKey, displayData);
            },
            getParentsRowKey: key => {
              const index = parseInt(key);
              const keys = [];
              const data = displayedData[index];
              let parentLevel = (data.data.level || 1) - 1;
              for (let rowIndex = index; rowIndex >= 0; rowIndex--) {
                const {
                  level
                } = displayedData[rowIndex].data;
                if (level === parentLevel) {
                  keys.push(displayedData[rowIndex].key);
                  --parentLevel;
                }
              }
              return keys;
            },
            onExpand: (node, expanded) => {
              const nodes = new Set(tableState.expandedNodes);
              if (nodes.has(node.id) && !expanded) nodes.delete(node.id);else if (!nodes.has(node.id) && expanded) nodes.add(node.id);
              const data = normalizedData(nodes, displayData);
              setDisplayedData(data);
              onExpand(node, expanded);
              setNumberOfRows(data.length);
            },
            renderCellData: ({
              rowIndex,
              columnKey,
              columnIndex,
              disabled
            }) => {
              const displayItem = displayedData[rowIndex];
              if (columnKey === 'info' || columnKey === 'infoIcon') {
                if (displayItem.data.infoIcon) {
                  return /*#__PURE__*/_jsx(Icon, {
                    ...displayItem.data.infoIcon,
                    testId: postfixTestId(props.testId, 'infoIcon'),
                    disabled: disabled
                  });
                } else if (displayItem.data.infoText) {
                  return (
                    /*#__PURE__*/
                    // special info column
                    _jsx(Icon, {
                      icon: Information,
                      testId: postfixTestId(props.testId, 'infoTextIcon'),
                      tooltip: displayItem.data.infoText,
                      onClick: event => {
                        event.preventDefault();
                        event.stopPropagation();
                      },
                      disabled: disabled
                    })
                  );
                } else {
                  return null;
                }
              }
              const keyFieldValue = displayItem.data[props.dataFieldKeys[columnIndex]];
              const count = displayItem.count;
              const targetDisplayText = keyFieldValue.toString() + (count > 1 ? ` (${count})` : '');
              const sourceDisplayText = keyFieldValue.toString();
              const alwaysSelected = props.tableName === 'target' && isAlwaysSelectedItem(displayItem.data);
              const bCustomItem = props.tableName === 'target' && displayItem.data._custom;
              const isSelected = props.selections?.includes(rowIndex.toString());
              return /*#__PURE__*/_jsx(motion.div, {
                variants: framerVariants,
                initial: "hidden",
                animate: "visible",
                exit: "hidden",
                children: /*#__PURE__*/_jsxs(Flow, {
                  spacing: "xs",
                  className: cn.flowContainer,
                  children: [/*#__PURE__*/_jsx(Text, {
                    className: cx(cn.text, isSelected && cn.selected),
                    wrapText: false,
                    disabled: disabled,
                    children: props.tableName === 'target' ? targetDisplayText : sourceDisplayText
                  }), alwaysSelected ? /*#__PURE__*/_jsx(Icon, {
                    className: cn.icon,
                    icon: LockClosed,
                    testId: postfixTestId(props.testId, 'icon'),
                    tooltip: localize('lockClosed.tooltip.label')
                  }) : null, bCustomItem ? /*#__PURE__*/_jsx(Icon, {
                    className: cn.icon,
                    icon: Choice,
                    tooltip: localize('customItem.choiceIcon.tooltip.label'),
                    testId: postfixTestId(props.testId, 'choiceIcon')
                  }) : null]
                })
              });
            },
            selectionMode: "desktop",
            onSelect: (rows, selectAll) => {
              const selectAllInDisplayData = selectAll === true ? canBeSelectAllInDisplayData(props.displayStyle, displayData) ? true : 'mixed' : selectAll;
              const rowsInDisplayData = selectAll === true && (!rows || rows.size === 0) ? displayData.map((_displayItem, index) => index.toString()) : Array.from(rows ?? []);

              // do not allow folders to be selected
              rowsInDisplayData.filter(rowIndex => displayData[parseInt(rowIndex)]?.data.hasChildren === true);
              setSelectionState({
                selectAll: selectAllInDisplayData,
                selectedRows: rowsInDisplayData ? [...rowsInDisplayData] : undefined
              });
            },
            isRowDisabled: key => props.tableName !== 'target' && (props.data[parseInt(key)]?._disabled ?? false),
            initialFocusVisible: !props.preventInitialAutoFocus && props.hideSearch && props.tableName === 'source' || false,
            initialFocus: !props.preventInitialAutoFocus && props.tableName === 'source' ? selectionsInDisplayData && selectionsInDisplayData.length > 0 && props.focusAfterAction ? (() => {
              const selectionsInDisplayedData = getSelectionsInDisplayedData(selectionsInDisplayData, displayedData);
              return {
                rowIndex: selectionsInDisplayedData[0] > 0 ? selectionsInDisplayedData[0] : focusedCell.current.rowIndex,
                region: TableRegions.Data,
                colIndex: 0
              };
            })() : focusedCell.current.rowIndex < 0 ? undefined : focusedCell.current : undefined,
            onFocusChange: focus => {
              focusedCell.current = focus;
            },
            cellBordersVisible: false,
            columnHeadersVisible: false,
            onDoubleClick: ({
              rowIndex,
              rowKey
            }) => {
              const itemIndex = props.displayStyle === 'single' ? rowIndex : parseInt(rowKey);
              props.onDoubleClick(displayData[itemIndex].originRowIndex, props.tableName);
            },
            emptyDataProps: {
              text: zeroStateText
            },
            hideManageColumnsButton: true,
            rowHeight: rowIndex => {
              if (descriptionField && displayedData[rowIndex]?.data[descriptionField]) {
                return locale === 'zh-cn-pseudo' ? 42 : 40;
              } else {
                return 28;
              }
            },
            removeFillRows: true,
            accessibilityInfo: {
              getCellInfo: props => {
                let message = null;
                let prefix = '';
                const text = [];
                const itemData = displayedData[props.rowIndex]?.data;
                if (props.columnIndex === 0) {
                  let icon;
                  if (itemData) {
                    if (itemData.icon) {
                      icon = itemData.icon;
                      const svgIcon = isSVGIconDescriptor(icon);
                      const {
                        ...iconA11yInfo
                      } = svgIcon ? getIconA11yInfo({
                        icon: itemData.icon
                      }) : getIconA11yInfo(icon);
                      if (iconA11yInfo?.ariaLabel && iconA11yInfo?.ariaLabel !== itemData[key].toString()) {
                        iconA11yInfo?.ariaLabel && text.push(iconA11yInfo.ariaLabel);
                      }
                    }
                    if (customItems?.includes(itemData)) {
                      message = localize('customItemSelection.aria', {
                        customItemSelected: itemData[key].toString()
                      });
                    } else if (descriptionField && itemData[descriptionField]) {
                      message = `${itemData[key].toString()} ${itemData[descriptionField].toString()}`;
                    } else {
                      message = itemData[key].toString();
                    }
                    if (itemData._alwaysSelected || itemData.alwaysSelected) {
                      prefix = localize('lockClosed.aria');
                      text.push(prefix);
                    }
                  }
                  return text.length > 0 ? `${text.join(', ')} ${message}` : message;
                } else if (props.columnIndex === 1) {
                  if (itemData.infoIcon) {
                    const {
                      ...iconA11yInfo
                    } = getIconA11yInfo(itemData.infoIcon);
                    if (iconA11yInfo?.ariaLabel) {
                      return `${iconA11yInfo.ariaLabel}, ${itemData[key].toString()}`;
                    }
                  } else if (itemData.infoText) {
                    // add info icon text provided by consumer
                    return `${itemData.infoText}, ${itemData[key].toString()}`;
                  }
                  return null;
                }
                return null;
              }
            }
            // TODO: remove. temp workaround for NOVACOMMONS-12044
            ,
            setSelectAllRows: numberOfRows === 0 ? undefined : tableState.setSelectAllRows
          }) : null
        })
      })
    })]
  });
});
export function generateSingleDisplayData(originData) {
  const displayData = [];
  originData.forEach((item, index) => {
    const displayDataIndex = displayData.findIndex(displayItem => JSON.stringify(item) === JSON.stringify(displayItem.data));
    if (displayDataIndex <= -1) {
      displayData.push({
        data: item,
        count: 1,
        originRowIndex: index,
        key: `${index}`
      });
    } else {
      displayData[displayDataIndex].count++;
      // if duplicated items found, the originRowIndex will always indicate the last one.
      displayData[displayDataIndex].originRowIndex = index;
    }
  });
  return displayData;
}

// In a tree, selected and displayed indices may differ due to exp/coll nodes. we use the index based on the visible data (displayedData).
const getSelectionsInDisplayedData = (selectionsInDisplayData, displayedData) => selectionsInDisplayData.map(key => displayedData.findIndex(data => key === data.key));
function getSelectionsInDisplayData(selectionsInOriginData, originData, displayData) {
  const result = selectionsInOriginData.map(selection => {
    const originRowIndex = parseInt(selection);
    let displayItemIndex = displayData.findIndex(displayItem => {
      return displayItem.originRowIndex === originRowIndex;
    });
    if (displayItemIndex === -1) {
      const originItem = originData[originRowIndex];
      displayItemIndex = displayData.findIndex(displayItem => {
        return displayItem.data === originItem;
      });
    }
    return displayItemIndex.toString();
  });
  return result;
}
function haveDuplicatedItems(displayItems) {
  return displayItems.findIndex(displayItem => displayItem.count > 1) > -1;
}
function canBeSelectAllInDisplayData(displayStyle = 'multiple', displayData) {
  return displayStyle === 'multiple' || !haveDuplicatedItems(displayData);
}
//# sourceMappingURL=DualSelectorLayout.js.map